/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * PostStreamReviewScreen — floating toolbar shown after an AI session ends.
 *
 * Reuses the AI modal infrastructure (decoration plugin + ModalRegion) to render
 * a post-stream review toolbar with the following affordances:
 *   - Undo / Redo (operate on stored slices, NOT the editor history plugin)
 *   - View changes (toggle a diff overlay for the current segment)
 *   - Ask Rovo (a small prompt input)
 *   - Step through changes (reveals Previous / Next navigation between AI change segments)
 *
 * Only mounted when `useUIState` returns `'postStreamReview'`, which requires
 * both `platform_editor_ai_xstate_migration` and
 * `platform_editor_ai_new_aifc_editor_experience` gates to be ON.
 *
 * File layout (this directory):
 *   - `index.tsx` (this file) — screen component, plugin-state wiring, action handlers
 *   - `AskRovo.tsx` — prompt input pill with inline step-through navigation,
 *     show/hide changes toggle and Undo/Redo controls
 *   - `AcceptedToast.tsx` — the "Accepted" confirmation pill shown after accepting all changes
 *   - `MiniAcceptedToast.tsx` — the compact "Accepted" pill shown per-segment while stepping through
 *   - `hooks.ts` — `useOutsideClickClose`, `useHideDiffOnUnmount`, `useSegmentNavigation`
 *   - `icons.tsx` — hoisted icon render functions (stable refs)
 *   - `messages.ts` — i18n message descriptors
 *
 * Note: CSS style objects (`wrapperCSS`, `toolbarCSS`) live inline in this
 * file rather than a separate `styles.ts` — `@compiled/react`'s build-time
 * transform requires `css({})` calls to be co-located with the JSX that
 * consumes them, so a cross-file styles module is not supported.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { getPopupContainerFromEditorView } from '@atlaskit/editor-common/experiences';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';

import type { AIPlugin } from '../../../../../aiPluginType';
import { openPostStreamReviewModal } from '../../../../../editor-actions/actions';
import {
	resetSessionState,
	setReworkHighlightRange,
	setStreamReviewSegments,
	startAggregateRework,
	startFreeGenStream,
	startSegmentRework,
} from '../../../../../pm-plugins/ai-session-context/commands';
import type { AIContentPositionEntry } from '../../../../../pm-plugins/ai-session-context/types';
import { selectionPluginKey } from '../../../../../pm-plugins/selection/selection-plugin-key';
import { useContentComponentContext } from '../../../../hooks/useContentComponentContext';
import { ModalRegionErrorBoundary } from '../../../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { ModalRegion } from '../../../ModalRegion/ModalRegion';
import type { Action } from '../../hooks/useUIState';
import { scrollRangeIntoView } from '../../utils/reviewScroll';
import { calculateDiffSegments } from '../calculateDiffSegments';
import { calculateTopLevelNodeSegments } from '../calculateTopLevelNodeSegments';
import { computeUndoRedo } from '../computeUndoRedo';
import {
	buildDiffParamsForAllSegments,
	buildDiffParamsForSegment,
	mergeOverlappingSegments,
} from '../diff-helpers';
import { entryCanRedo, entryCanUndo, entryHasNoChange } from '../segmentSlices';

import { AcceptedToast } from './AcceptedToast';
import { AskRovo } from './AskRovo';
import {
	useHideDiffOnUnmount,
	useOutsideClickClose,
	useReviewKeyboardShortcuts,
	useSegmentNavigation,
} from './hooks';
import { MiniAcceptedToast } from './MiniAcceptedToast';
/**
 * `display: contents` — the wrapper exists purely to hold the ref used by the
 * outside-click handler and the screen testId, without introducing a box that
 * affects layout. All visible chrome is on `toolbarCSS` further inside.
 */
const wrapperCSS = css({ display: 'contents' });

/**
 * Compact single-row toolbar — see file-level JSDoc for the layout sketch.
 *
 * Fills its container (`ModalRegion`) so it tracks the modal width rather than
 * duplicating a fixed value.
 */
const toolbarCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: '100%',
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.xxlarge', '16px'),
	boxShadow: token('elevation.shadow.overlay'),
	boxSizing: 'border-box',
});

/**
 * Fallback height (px) used to reserve room below the review anchor when the
 * modal DOM node never reports a height (e.g. it stays hidden). The toolbar is a
 * fixed single row, so this is a safe approximation.
 */
const DEFAULT_REVIEW_MODAL_HEIGHT = 56;

/**
 * `ModalRegion` keeps the modal `display: none` (height 0) until it has measured
 * and positioned itself a couple of renders after open. We poll a few frames for
 * a real height before scrolling so the reserved room matches the toolbar — else
 * the first open under-scrolls and the modal overlaps the last change.
 */
const MODAL_MEASURE_MAX_FRAMES = 2;

/** DOM class the modal chrome is rendered with (see `ModalRegion`). */
const MODAL_CLASS_SELECTOR = '.ak-editor-plugin-ai-modal';

/**
 * Map a visible-list index (into the accepted-filtered `segments`) to its index
 * in the raw `streamReviewSegments`. The reducer addresses the raw array; the UI
 * navigates the filtered one, and the two diverge once anything is accepted.
 * Positional (the Nth non-accepted entry), not `indexOf`, so shared entries
 * can't match the wrong occurrence. Returns `-1` when out of range.
 */
/**
 * Selects the first usable decoration to anchor the modal to. Mirrors the helper
 * in `Prompt.tsx` so the modal positioning logic stays consistent across screens.
 */
function findModalDecoration(modalDecorationSet?: DecorationSet): Decoration | undefined {
	if (!modalDecorationSet) {
		return undefined;
	}
	return (
		modalDecorationSet
			.find(
				undefined,
				undefined,
				(spec) =>
					spec.key === 'modalMountPointDecoration' || spec.key === 'modalMountPointRangeDecoration',
			)
			.shift() ||
		modalDecorationSet
			.find(undefined, undefined, (spec) => spec.key === 'ai-nodeDecoration')
			.shift()
	);
}

/**
 * PostStreamReviewScreen — see file-level JSDoc for the full feature description.
 */
/**
 * [CCI-17658] Resolve a flat navigation index (into the derived `segments` list)
 * to the containing top-level segment in `streamReviewSegments`. Returns the
 * top-level index and the segment itself, or `undefined` if not found.
 */
function resolveTopLevelSegment(
	flatIndex: number,
	streamReviewSegments: AIContentPositionEntry[],
): { topLevelIndex: number; topLevelSegment: AIContentPositionEntry } | undefined {
	// When diff-based segmenting is off, flat index === top-level index.
	if (!fg('platform_editor_ai_diff_based_segmenting')) {
		return streamReviewSegments[flatIndex]
			? { topLevelIndex: flatIndex, topLevelSegment: streamReviewSegments[flatIndex] }
			: undefined;
	}
	let flatCounter = 0;
	for (let i = 0; i < streamReviewSegments.length; i++) {
		const entry = streamReviewSegments[i];
		if (entry.isAccepted) {
			continue;
		}
		const visibleDiffCount =
			entry.diffSegments?.filter((diffSegment) => !diffSegment.isAccepted).length || 0;
		// Top-level entries with nested diffs contribute only their visible diff
		// children; if all are accepted they contribute zero display slots.
		// Fallback to 1 means this top-level entry still occupies one step-through item.
		const effectiveCount = entry.diffSegments ? visibleDiffCount : 1;
		if (flatIndex >= flatCounter && flatIndex < flatCounter + effectiveCount) {
			return { topLevelIndex: i, topLevelSegment: entry };
		}
		flatCounter += effectiveCount;
	}
	return undefined;
}

export function PostStreamReviewScreen({
	api,
	dispatch,
	appearance,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	appearance: EditorAppearance;
	dispatch: React.Dispatch<Action>;
}): JSX.Element | null {
	const {
		editorView,
		wrapperElement,
		endExperience,
		editorPluginAIProvider,
		isInDocumentStreamingEnabled,
	} = useContentComponentContext();

	const {
		lastTriggeredFrom,
		modalMountedTimeStamp,
		modalDecorationSet,
		aiContentPositions,
		streamReviewSegments,
		currentStreamReviewSegmentIndex,
		currentStreamReviewMode,
		configItem,
	} = useSharedPluginStateWithSelector(api, ['aiExperience'], ({ aiExperienceState }) => ({
		lastTriggeredFrom: aiExperienceState?.aiDecorationExperiencePluginState.lastTriggeredFrom,
		modalMountedTimeStamp:
			aiExperienceState?.aiDecorationExperiencePluginState.modalMountedTimeStamp,
		modalDecorationSet: aiExperienceState?.aiDecorationExperiencePluginState.modalDecorationSet,
		aiContentPositions: aiExperienceState?.aiSessionPluginState?.aiContentPositions,
		streamReviewSegments: aiExperienceState?.aiSessionPluginState?.streamReviewSegments,
		currentStreamReviewSegmentIndex:
			aiExperienceState?.aiSessionPluginState?.currentStreamReviewSegmentIndex,
		currentStreamReviewMode: aiExperienceState?.aiSessionPluginState?.currentStreamReviewMode,
		configItem: aiExperienceState?.aiDecorationExperiencePluginState.configItem,
	}));

	// Fallback free-gen prompt input (used when product has no Rovo onSubmit).
	const [textInput, setTextInput] = useState('');
	// Whether the prompt currently holds any user input. Tracked separately from
	// `textInput` because the Rovo prompt's text never surfaces here — it only
	// reports emptiness (see `onPromptContentChange`) — while the fallback
	// `PromptScreen` reports the full string (see `onTextChange`). Drives whether
	// Enter accepts (empty) or submits the re-prompt (has text).
	const [hasInputText, setHasInputText] = useState(false);
	const textInputRef = useRef(textInput);
	textInputRef.current = textInput;

	// `cancel` is wired into `RovoPromptScreenWithLogic` / `PromptScreen`
	// which can invoke it on benign events (Esc inside the prompt input, click
	// outside the prompt input but still inside the toolbar, etc.). For the
	// Post Stream Review screen we explicitly DO NOT want any of those to tear
	// down the modal — the user is still actively reviewing AI changes and the
	// toolbar must stay anchored until they take an explicit dismiss action.
	const cancel = useCallback(
		(_options?: {
			shouldClearSelectionContext?: boolean;
			shouldIgnoreNextSpaceTrigger?: boolean;
		}) => {
			// no-op — see comment above
		},
		[],
	);

	// AI content range exposed to Rovo as selection context on rework submit.
	// Populated below (needs the segments/mode) and read via a ref so the earlier
	// submit callbacks can reference it without a declaration-order problem.
	const reworkContextRangeRef = useRef<{ endPos: number; startPos: number } | null>(null);

	// Set the Rovo selection context for a rework prompt without installing an
	// editor selection (which would show the floating toolbar). A synthetic
	// `TextSelection` lets the AI selection plugin derive the `SelectionContext`
	// for `getAISelectionContext()` / `ReadContentCommandHandler`; we only
	// `setMeta`, never `setSelection`, so the editor selection is unchanged.
	const setSelectionContextForRework = useCallback(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		const range = reworkContextRangeRef.current;
		if (!range) {
			return;
		}
		const { state } = editorView;
		const clampedFrom = Math.min(range.startPos, state.doc.content.size);
		const clampedTo = Math.min(range.endPos, state.doc.content.size);
		if (clampedFrom >= clampedTo) {
			return;
		}
		const selection = TextSelection.create(state.doc, clampedFrom, clampedTo);
		const tr = state.tr.setMeta(selectionPluginKey, {
			action: 'lastSelectionContext',
			selection,
		});

		tr.setSelection(selection);

		// Paint a highlight over the reworked range so the author sees what the AI is
		// about to work on. Owned by the ai-session-context plugin so it renders
		// mutually-exclusively with the undone-segment gray. `setReworkHighlightRange`
		// is an EditorCommand `({ tr }) => tr`, so applying it folds its meta onto this
		// same transaction. This screen only mounts under the New AIFC Experience.
		setReworkHighlightRange({ startPos: clampedFrom, endPos: clampedTo })({ tr });

		editorView.dispatch(tr);
	}, [editorView]);

	const onTextChange = useCallback((input: string) => {
		setTextInput(input);
		setHasInputText(input.trim().length > 0);
	}, []);

	// The Rovo prompt has no `onTextChange`; it reports emptiness via
	// `onPromptContentChange`. A mention/inline card counts as content even when
	// there's no plain text, so Enter submits the re-prompt rather than accepting.
	const onPromptContentChange = useCallback((payload: PromptContentChangePayload) => {
		setHasInputText(!payload.isEmpty || payload.hasMentionOrInlineCard);
	}, []);

	// Mirror Prompt.tsx — decide Rovo vs fallback prompt presentation.
	const hasRovoPrompt = !!editorPluginAIProvider.onSubmit;
	const { PromptEditor } = editorPluginAIProvider;
	const hasFallbackPrompt = !hasRovoPrompt && !!PromptEditor;

	const screenRef = useRef<HTMLDivElement>(null);
	const rawSegmentIndex = currentStreamReviewSegmentIndex ?? 0;
	// The per-tool-call entries with overlaps merged: when the AI edits the SAME
	// node across tool calls the raw ranges overlap, which corrupts reconstruction
	// (out-of-range steps / wrong undo). Merging ONCE here yields disjoint,
	// doc-tiling changes for every consumer — step-through segmenting and all
	// aggregate operations. Memoized so its identity is stable for the deps below.
	const coarseSegments = useMemo(
		() => mergeOverlappingSegments(aiContentPositions ?? []),
		[aiContentPositions],
	);
	const intl = useIntl();

	const isStepThroughExpanded = currentStreamReviewMode === 'stepThrough';

	// Two review modes, driven by the step-through toggle (no separate control):
	//   - AGGREGATE (collapsed, default): View changes / Undo / Redo act on ALL the
	//     still-under-review changes at once, over the fine `visibleSegments`.
	//   - STEP-THROUGH (expanded): navigate the `visibleSegments` one at a time.
	const isAggregate = !isStepThroughExpanded;

	// Authoritative stream-review storage: top-level segments (with optional nested
	// diff-level sub-segments). This includes accepted entries.
	const allTopLevelSegments = useMemo(() => streamReviewSegments ?? [], [streamReviewSegments]);

	// Step-through display list: when diff-based segmenting is ON, flatten nested
	// diff-level entries; otherwise keep top-level entries. Accepted top-level
	// segments are hidden from both modes.
	const displaySegments = useMemo(() => {
		const visibleTopLevel = allTopLevelSegments.filter((s) => !s.isAccepted);
		if (!fg('platform_editor_ai_diff_based_segmenting')) {
			return visibleTopLevel;
		}
		return visibleTopLevel.flatMap((topLevel) =>
			topLevel.diffSegments
				? topLevel.diffSegments.filter((diffSegment) => !diffSegment.isAccepted)
				: [topLevel],
		);
	}, [allTopLevelSegments]);
	const segmentIndex =
		displaySegments.length > 0
			? Math.max(0, Math.min(rawSegmentIndex, displaySegments.length - 1))
			: 0;

	// Signature of every displayed segment's `appliedIndex`. Undo/redo/rework change
	// `appliedIndex` in place without moving `segmentIndex` or `displaySegments.length`,
	// so the diff effect below (keyed on the active view) wouldn't otherwise re-fire.
	// This changes on exactly those slice-history steps and is a precise repaint trigger.
	const appliedIndexSignature = useMemo(
		() => displaySegments.map((segment) => segment.appliedIndex).join(','),
		[displaySegments],
	);

	const submit = useCallback(() => {
		const currentInput = textInputRef.current;
		if (!currentInput?.trim()) {
			return;
		}
		setSelectionContextForRework();
		// [CCI-17658] Per-segment rework: preserve segment state and snapshot
		// the active top-level segment for in-bounds detection.
		if (!isAggregate && streamReviewSegments?.length) {
			const resolved = resolveTopLevelSegment(segmentIndex, streamReviewSegments);
			if (resolved) {
				api?.core?.actions.execute(
					startSegmentRework(
						resolved.topLevelIndex,
						resolved.topLevelSegment.startPos,
						resolved.topLevelSegment.endPos,
						currentInput,
					),
				);
				return;
			}
		}
		// Aggregate rework: start fresh, clearing stale Post Stream Review state.
		api?.core?.actions.execute(startAggregateRework(currentInput));
	}, [
		api?.core?.actions,
		setSelectionContextForRework,
		isAggregate,
		streamReviewSegments,
		segmentIndex,
	]);

	// Compute the working segments. Both strategies start from the top-level-node
	// segments (`calculateTopLevelNodeSegments`, one per changed top-level node):
	//   - gate OFF (`block`) → use those top-level segments as-is.
	//   - gate ON  (`smart`) → refine EACH top-level segment INDEPENDENTLY into
	//     granular (sentence / paragraph / node) sub-segments. Running smart per
	//     segment keeps its `mergeOverlappingByNewDocRange` scoped to a single node,
	//     so distinct top-level nodes are never collapsed into one segment.
	// [CCI-17658] Compute working segments with diff sub-segments NESTED under
	// their parent top-level segment (not flatMapped). This preserves the grouping
	// needed for per-segment rework targeting: when reworking a diff sub-segment,
	// the parent top-level segment identifies the scope.
	const computeWorkingSegments = useCallback((): AIContentPositionEntry[] => {
		if (!editorView || editorView.isDestroyed || coarseSegments.length === 0) {
			return [];
		}

		const topLevelSegments = calculateTopLevelNodeSegments(coarseSegments);
		if (!fg('platform_editor_ai_diff_based_segmenting')) {
			// Drop top-level segments that carry no reviewable change
			// (original slice === applied slice), so navigation never stops on a
			// "change" where nothing actually changed.
			return topLevelSegments.filter((segment) => !entryHasNoChange(segment));
		}
		// Nest diff-level sub-segments under each top-level segment instead of
		// flatMapping them. A top-level segment that can't sub-divide (whole-node
		// add/remove, or a change the diff strategy doesn't surface) keeps
		// `diffSegments` as an empty array.
		return topLevelSegments.reduce<AIContentPositionEntry[]>((acc, segment) => {
			const params = buildDiffParamsForSegment(editorView, segment);
			if (!params || params.steps.length === 0) {
				// Can't sub-divide (e.g. whole-node add/remove): keep the top-level
				// segment as the reviewable unit — it is a genuine change.
				acc.push(segment);
				return acc;
			}
			const granular = calculateDiffSegments({
				originalDoc: params.originalDoc,
				steps: params.steps,
				diffType: 'smart',
				locale: intl.locale,
			});
			// Filter the granular sub-segments down to genuine changes.
			// If none survive, the whole top-level segment carries no change and is
			// dropped; otherwise keep it with only the changed sub-segments nested.
			const changedGranular = granular.filter((sub) => !entryHasNoChange(sub));
			if (changedGranular.length > 0) {
				acc.push({ ...segment, diffSegments: changedGranular });
			} else if (granular.length === 0) {
				// The diff strategy surfaced no sub-segments at all: fall back to the
				// top-level segment as the reviewable unit.
				acc.push(segment);
			}
			return acc;
		}, []);
	}, [editorView, coarseSegments, intl.locale]);

	const computeWorkingSegmentsRef = useRef(computeWorkingSegments);
	// Keep the ref in sync via a effect rather than assigning during render:
	// mutating a ref in the render body is a render-phase side effect that is unsafe
	// under React concurrent mode (renders can be interrupted/replayed).
	useEffect(() => {
		computeWorkingSegmentsRef.current = computeWorkingSegments;
	}, [computeWorkingSegments]);

	// Bootstrap: compute the working segments ONCE when the review opens and
	// publish them to plugin state. Guarded on `segments` being empty so it never
	// re-runs mid-review (which would clobber per-segment undo state) — the plugin
	// keeps positions remapped from there. A fresh AI session clears
	// `streamReviewSegments` (END_SESSION / RESET), so the next open recomputes.
	useEffect(() => {
		if (
			!editorView ||
			editorView.isDestroyed ||
			// Guard on the RAW plugin-state list (not the accepted-filtered
			// `segments`) so accepting every-but-one segment never re-triggers the
			// one-time bootstrap and clobbers per-segment undo/accept state.
			allTopLevelSegments.length > 0 ||
			coarseSegments.length === 0
		) {
			return;
		}
		const computed = computeWorkingSegmentsRef.current();
		if (computed.length > 0) {
			// Stamp document reading order (0-based) now, while ranges are distinct.
			// `computeWorkingSegments` returns segments in ascending `startPos`, so
			// the array index IS the reading order. Aggregate Undo/Redo relies on
			// this to re-order segments after their live `startPos` collapses.
			// Also stamp a `groupId` per contiguous run: a gap between a segment's
			// `startPos` and the previous segment's `endPos` starts a new group. This
			// lets undo/redo re-materialise an undone `add` at the right place among
			// its run siblings once their live positions collapse to one point.
			let groupId = 0;
			const ordered = computed.map((segment, order) => {
				if (order > 0 && segment.startPos !== computed[order - 1].endPos) {
					groupId += 1;
				}
				return { ...segment, order, groupId };
			});
			api?.core?.actions.execute(setStreamReviewSegments(ordered));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorView, coarseSegments, allTopLevelSegments.length, api]);

	const currentSegment = displaySegments[segmentIndex];
	// [CCI-17658] Step-through can display diff-level entries, but rework history
	// is tracked on the containing top-level segment. Resolve the active top-level
	// segment once and reuse it for range targeting and undo/redo state.
	const activeTopLevel = useMemo(
		() =>
			!isAggregate && streamReviewSegments?.length
				? resolveTopLevelSegment(segmentIndex, streamReviewSegments)
				: undefined,
		[isAggregate, streamReviewSegments, segmentIndex],
	);
	const activeStepSegment = activeTopLevel?.topLevelSegment ?? currentSegment;
	// Total reviewable changes — drives whether the "Step through changes" toggle
	// is offered (ActionButtons hides it for <= 1) and the "N / total" counter.
	const totalSegments = displaySegments.length;

	// Keep the rework selection-context range in sync with the current view:
	// aggregate = the span of all still-visible (non-accepted) changes
	// (min/max across `segments`); step-through = the active segment's range.
	if (isAggregate) {
		reworkContextRangeRef.current = displaySegments.length
			? {
					startPos: Math.min(...displaySegments.map((s) => s.startPos)),
					endPos: Math.max(...displaySegments.map((s) => s.endPos)),
				}
			: null;
	} else {
		reworkContextRangeRef.current = activeStepSegment
			? { startPos: activeStepSegment.startPos, endPos: activeStepSegment.endPos }
			: null;
	}

	// No effect sets the editor selection while the Review moment is open: a real
	// selection would show the floating toolbar and can't represent edits spanning
	// non-contiguous ranges. Instead the diff overlay is always shown (below), and
	// the Rovo selection context is set only on Ask Rovo submit
	// (`setSelectionContextForRework`), so the toolbar stays hidden.

	// Undo/Redo state is **derived** from each segment's `appliedIndex`, so the
	// correct button state is restored across Prev/Next navigation.
	//   - Aggregate mode acts on ALL still-visible (non-accepted) changes at once,
	//     operating on the fine `segments`: Undo is enabled while ANY visible change
	//     can step back (appliedIndex > 0), Redo while ANY can step forward
	//     (appliedIndex < slices.length - 1).
	//   - Step-through acts on the single active visible segment.
	const canUndo = isAggregate
		? displaySegments.some((s) => entryCanUndo(s))
		: !!currentSegment && entryCanUndo(currentSegment);
	const canRedo = isAggregate
		? displaySegments.some((s) => entryCanRedo(s))
		: !!currentSegment && entryCanRedo(currentSegment);

	// Scroll a `[startPos, endPos]` change range into the viewport, deferred one
	// frame so the re-anchored modal has laid out (its height reserves room below
	// the anchor). No-ops when the range is already visible; when it is taller than
	// the viewport it lands on the END (scrolling down) or the START (scrolling up)
	// so the relevant edge is never cut off.
	const scrollRangeIntoViewDeferred = useCallback(
		(startPos: number, endPos: number) => {
			if (!editorView || editorView.isDestroyed) {
				return;
			}
			// Poll a few frames for the modal to lay out (it is `display: none`,
			// height 0, until `ModalRegion` positions it) so the reserved room below
			// the anchor matches the real toolbar height. Scroll as soon as it has a
			// height, or fall back after the cap so we never wait indefinitely.
			let frames = 0;
			const measureAndScroll = () => {
				if (!editorView || editorView.isDestroyed) {
					return;
				}
				const modalEl = editorView.dom.ownerDocument.querySelector(MODAL_CLASS_SELECTOR);
				const measured = modalEl instanceof HTMLElement ? modalEl.offsetHeight : 0;
				if (measured === 0 && frames < MODAL_MEASURE_MAX_FRAMES) {
					frames += 1;
					requestAnimationFrame(measureAndScroll);
					return;
				}
				scrollRangeIntoView({
					view: editorView,
					startPos,
					endPos,
					modalHeight: measured || DEFAULT_REVIEW_MODAL_HEIGHT,
				});
			};
			requestAnimationFrame(measureAndScroll);
		},
		[editorView],
	);

	const onToggleStepThrough = useCallback(() => {
		// Compute the next mode from current state and update it with a
		// plain (non-functional) setState so the state updater stays pure. The
		// side-effect (dispatching the modal command) is performed exactly once
		// here, NOT inside the updater — React may invoke functional updaters more
		// than once (e.g. StrictMode dev double-invoke), which would double-dispatch.
		const next = !isStepThroughExpanded;
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		// The working `segments` already live in plugin state (computed on open and
		// shared by both modes), so toggling only re-anchors the modal — no
		// segment (re)computation here.
		if (next) {
			// Entering step-through: start at the first segment.
			const firstSegment = displaySegments[0];
			const startPos = firstSegment?.startPos;
			const endPos = firstSegment?.endPos;
			openPostStreamReviewModal({
				editorView,
				mode: 'stepThrough',
				segmentData: {
					segmentIndex: 0,
					positions: { startPos, endPos },
				},
			});
			scrollRangeIntoViewDeferred(startPos, endPos);
		} else {
			// Returning to AGGREGATE mode: re-anchor below the LAST visible segment
			// and span the highlight across ALL still-visible (non-accepted)
			// changes, so accepted segments neither anchor nor widen the span.
			const aggregateStart = displaySegments.length
				? Math.min(...displaySegments.map((s) => s.startPos))
				: 0;
			const aggregateEnd = displaySegments.length
				? Math.max(...displaySegments.map((s) => s.endPos))
				: 0;
			openPostStreamReviewModal({
				editorView,
				mode: 'aggregate',
				segmentData: {
					segmentIndex: Math.max(0, displaySegments.length - 1),
					positions: { startPos: aggregateStart, endPos: aggregateEnd },
				},
			});
		}
	}, [editorView, displaySegments, isStepThroughExpanded, scrollRangeIntoViewDeferred]);

	// Real teardown: end the experience, wipe the session state, and terminate the
	// UI. Used by the Rovo re-prompt path and, after the "Accepted" pill's lifetime,
	// by `acceptAndConfirm` below.
	const finalizeClose = useCallback(() => {
		endExperience({ shouldClearSelectionContext: true });
		api?.core?.actions.execute(resetSessionState());
		dispatch({ type: 'terminate' });
	}, [endExperience, api, dispatch]);

	const onBeforeSubmitForRovoChat = useCallback(
		(_prompt: DocNode): boolean => {
			setSelectionContextForRework();

			// Per-segment rework: preserve segment state and snapshot the active
			// top-level segment for in-bounds detection.
			let didStartSegmentRework = false;
			if (!isAggregate && streamReviewSegments?.length) {
				const resolved = resolveTopLevelSegment(segmentIndex, streamReviewSegments);
				if (resolved) {
					api?.core?.actions.execute(
						startSegmentRework(
							resolved.topLevelIndex,
							resolved.topLevelSegment.startPos,
							resolved.topLevelSegment.endPos,
							'',
						),
					);
					didStartSegmentRework = true;
				}
			}
			// Aggregate rework: tear down but preserve selection context
			endExperience({ shouldClearSelectionContext: false });

			// Enter streaming immediately so SessionUI swaps PSR -> Loading right away.
			// Aggregate rework clears the stale review state; per-segment keeps it.
			api?.core?.actions.execute(
				didStartSegmentRework ? startFreeGenStream(textInput) : startAggregateRework(textInput),
			);

			// Allow default inline prompt submit to continue.
			return false;
		},
		[
			setSelectionContextForRework,
			endExperience,
			isAggregate,
			streamReviewSegments,
			segmentIndex,
			api?.core?.actions,
			textInput,
		],
	);

	const openInRovoChatConfig = useMemo(
		() => ({ onBeforeSubmit: onBeforeSubmitForRovoChat }),
		[onBeforeSubmitForRovoChat],
	);

	/**
	 * Undo/Redo for both modes, via the Option F forward-walk (`computeUndoRedo`).
	 * It applies the slice-replaces, sets each touched segment's `appliedIndex`,
	 * writes authoritative positions, and re-anchors the modal — all in a SINGLE
	 * transaction (through `beforeTransform`, which runs at the start of
	 * `createOpenAIModalCommand`'s transform). That avoids a two-dispatch race and
	 * a sub-frame window where `canUndo`/`canRedo` or the diff read a stale state.
	 *
	 * Positions are derived from the transform itself (never a remapped-then-
	 * collapsed stored value), so contiguous same-anchor changes re-separate
	 * correctly on redo. See `computeUndoRedo` for the mechanic.
	 */
	const replaceCurrentSegmentWith = useCallback(
		(direction: 'undo' | 'redo') => {
			// Aggregate mode acts on ALL non-accepted segments and does NOT depend on
			// `currentSegment` (which may be undefined if `segmentIndex` is stale).
			// Only step-through requires a valid `currentSegment`.
			if (!editorView || (!isAggregate && !activeStepSegment)) {
				return;
			}

			// The `appliedIndex` each segment should end at. Option F is agnostic to
			// WHICH slice — it only positions the replace and records the range:
			//   - AGGREGATE (Semantic X): revert-all → 0, reapply-all → latest slice.
			//     Generalises the old all-at-once toggle; identical to it when every
			//     history is length 2 (no rework).
			//   - STEP-THROUGH: step ONLY the active (raw-indexed) segment by one.
			let targetIndexFor: (seg: AIContentPositionEntry, index: number) => number;
			if (isAggregate) {
				targetIndexFor = direction === 'undo' ? () => 0 : (seg) => seg.slices.length - 1;
			} else {
				// Fast path when diff-based segmentation is OFF: keep the original
				// single-segment replace flow to avoid unnecessary re-anchor churn.
				if (!fg('platform_editor_ai_diff_based_segmenting') && currentSegment) {
					const delta = direction === 'undo' ? -1 : 1;
					const newIndex = currentSegment.appliedIndex + delta;
					const nextSlice = currentSegment.slices[newIndex];
					if (!nextSlice) {
						return;
					}
					openPostStreamReviewModal({
						editorView,
						segmentData: {
							segmentIndex,
							positions: { startPos: currentSegment.startPos, endPos: currentSegment.endPos },
						},
						appliedIndexUpdate: { updates: [{ index: segmentIndex, appliedIndex: newIndex }] },
						beforeTransform: (tr) => {
							try {
								tr.replaceRange(currentSegment.startPos, currentSegment.endPos, nextSlice);
							} catch {
								// keep modal reopen; range may be stale under concurrent edits
							}
							return tr;
						},
					});
					return;
				}

				// `computeUndoRedo` addresses the raw array, so translate the visible
				// index — otherwise, after an accept, undo/redo steps the wrong entry.
				const delta = direction === 'undo' ? -1 : 1;
				targetIndexFor = (seg, index) =>
					index === segmentIndex ? seg.appliedIndex + delta : seg.appliedIndex;
			}

			const plan = computeUndoRedo(editorView, displaySegments, targetIndexFor);
			if (!plan) {
				return;
			}

			// Apply the slice-replaces, set `appliedIndex`, write authoritative
			// positions, and re-anchor the modal — all atomically in ONE transaction.
			openPostStreamReviewModal({
				editorView,
				segmentData: { segmentIndex, positions: plan.anchor },
				appliedIndexUpdate: { updates: plan.appliedIndexUpdates },
				positionsUpdate: { positions: plan.positions },
				// Keep Review-moment Undo/Redo OUT of the editor history stack (see the
				// file-level note: undo/redo operate on stored slices, not the history plugin).
				// The slice-replace shares the transaction that re-anchors the modal; marking it
				// addToHistory:false stops prosemirror-history recording it as an entry, so the
				// whole AI session stays a single editor undo point — editor Undo (Cmd/Ctrl+Z)
				// after the review reverts the AI edit as a whole, not each review-moment step.
				// History still maps the changed ranges, keeping the AI-generation entry
				// consistent with the reviewed document.
				beforeTransform: (tr) => plan.beforeTransform(tr).setMeta('addToHistory', false),
			});
		},
		[editorView, activeStepSegment, currentSegment, segmentIndex, isAggregate, displaySegments],
	);

	// The diff overlay is always shown; `showDeletedChanges` only toggles DELETED
	// content (via "Compare with original"): `true` (default) shows deletions,
	// `false` => `hideDeletedDiffs: true`. Persists across Prev/Next and mode
	// switches so the user's chosen view is preserved through the review.
	const [showDeletedChanges, setShowDeletedChanges] = useState(true);
	// The show/hide-deletions toggle is enabled while any change is still applied
	// (aggregate) / the current segment is applied (per-segment).
	const canViewChanges = canUndo;

	// Pull the showDiff command bag into locals so the `useCallback` deps array
	// can reference the parent objects instead of destructured leaves (ESLint's
	// `exhaustive-deps` treats `api.showDiff.commands.hideDiff` as depending on
	// the whole `api.showDiff.commands` object).
	const showDiffCommands = api?.showDiff?.commands;
	const coreActions = api?.core?.actions;

	// Tear down the diff overlay. Used before an undo/redo: the diff must not be
	// painted while the undo/redo transaction is computed, because for a `remove`
	// segment the removed content is rendered as a diff decoration and leaving it
	// visible makes the undo/redo apply at the wrong position. After the modal
	// re-opens, the always-on diff effect re-shows the overlay.
	const hideDiff = useCallback(() => {
		if (showDiffCommands?.hideDiff && coreActions) {
			coreActions.execute(showDiffCommands.hideDiff);
		}
	}, [showDiffCommands, coreActions]);

	// Whether the review has been accepted and the "Accepted" pill is showing. While
	// true the toolbar is swapped for the pill (see render) and the review effects
	// below stand down; teardown is deferred until the pill finishes (`AcceptedToast`).
	const [isAccepted, setIsAccepted] = useState(false);
	const isAcceptedRef = useRef(isAccepted);
	isAcceptedRef.current = isAccepted;

	// Accept = keep the content already in the document. Hide the diff so the author
	// sees the final clean result, then show the "Accepted" pill and defer the real
	// teardown until it finishes. A repeat dismiss while the pill is up (e.g. clicking
	// into the document) finalises immediately instead of waiting out the timer.
	const acceptAndConfirm = useCallback(() => {
		if (isAcceptedRef.current) {
			finalizeClose();
			return;
		}
		hideDiff();
		setIsAccepted(true);
	}, [finalizeClose, hideDiff]);

	// Click-away accepts and closes the review. Routed through `acceptAndConfirm` so
	// it shows the "Accepted" pill first (or finalises if the pill is already up).
	useOutsideClickClose(screenRef, editorView, acceptAndConfirm);

	// The "Mini accepted" pill shown when a single change is accepted while stepping
	// through and more remain. Captures where the Review moment currently sits so the
	// mini can slide out of it while the toolbar re-anchors to the next segment. The
	// incrementing `key` restarts the animation on each successive accept.
	const [miniAccepted, setMiniAccepted] = useState<{
		anchorY: number;
		centerX: number;
		key: number;
		onSlideOut: () => void;
	} | null>(null);
	const miniKeyRef = useRef(0);
	const clearMiniAccepted = useCallback(() => setMiniAccepted(null), []);

	// If the review is being accepted-and-closed (e.g. click-away) while a mini's move
	// is still pending, drop the mini so its deferred `onSlideOut` can't re-anchor the
	// modal during teardown.
	useEffect(() => {
		if (isAccepted) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			setMiniAccepted(null);
		}
	}, [isAccepted]);

	const onUndo = useCallback(() => {
		hideDiff();
		replaceCurrentSegmentWith('undo');
	}, [hideDiff, replaceCurrentSegmentWith]);
	const onRedo = useCallback(() => {
		hideDiff();
		replaceCurrentSegmentWith('redo');
	}, [hideDiff, replaceCurrentSegmentWith]);

	// Accept the current change (press Enter).
	//   - Aggregate: nothing left to step through, so `acceptAndConfirm()` shows the
	//     "Accepted" pill and then tears down (content is already applied).
	//   - Step-through: mark the current segment accepted and re-anchor at the
	//     next visible segment (wrap last → first). When one change would remain,
	//     switch back to aggregate. The flip and re-anchor commit in one
	//     transaction via `acceptedUpdate`, then the newly-active segment is
	//     scrolled into view so the modal shows at the correct position. When the
	//     last change is accepted, `acceptAndConfirm()` runs the confirmation + close.
	const acceptCurrent = useCallback(() => {
		if (!editorView || editorView.isDestroyed) {
			return;
		}
		if (isAggregate) {
			acceptAndConfirm();
			return;
		}
		// Step-through: need a valid current visible segment to accept.
		if (!currentSegment) {
			return;
		}
		// Index of the accepted segment in the RAW plugin-state array — this is what
		// the reducer's `acceptedUpdate` flips. `segmentIndex` addresses the visible
		// (accepted-filtered) list, so translate it to the raw-array index.
		// Don't paint the diff while the modal re-anchors (mirrors undo/redo); the
		// always-on diff effect re-shows it after the re-open.
		hideDiff();

		// The visible list AFTER this accept drops the current segment.
		const remainingVisible = displaySegments.filter((_, i) => i !== segmentIndex);

		// Nothing left to review → accept completes the flow, so confirm + close the
		// modal (`hideDiff` already ran above).
		if (remainingVisible.length === 0) {
			acceptAndConfirm();
			return;
		}

		// A change is being accepted while others remain, so the Review moment stays
		// open and moves on. Build the "advance" step first, then play the "Mini
		// accepted" pill sliding out of the current toolbar and run the advance a beat
		// later (see below).
		let doMove: () => void;
		if (remainingVisible.length === 1) {
			// One change left → collapse to AGGREGATE over the single remaining change.
			const startPos = Math.min(...remainingVisible.map((s) => s.startPos));
			const endPos = Math.max(...remainingVisible.map((s) => s.endPos));
			doMove = () => {
				openPostStreamReviewModal({
					editorView,
					mode: 'aggregate',
					segmentData: { segmentIndex: 0, positions: { startPos, endPos } },
					acceptedUpdate: { index: segmentIndex },
				});
				scrollRangeIntoViewDeferred(startPos, endPos);
			};
		} else {
			// Advance to the NEXT visible segment (wrap last → first). Because the
			// accepted (current) segment is removed, the target's index in the shrunk
			// list is `segmentIndex % remainingVisible.length` (wraps to 0 when the
			// accepted one was last).
			const nextIndex = segmentIndex % remainingVisible.length;
			const nextSegment = remainingVisible[nextIndex];
			doMove = () => {
				openPostStreamReviewModal({
					editorView,
					segmentData: {
						segmentIndex: nextIndex,
						positions: { startPos: nextSegment.startPos, endPos: nextSegment.endPos },
					},
					acceptedUpdate: { index: segmentIndex },
				});
				scrollRangeIntoViewDeferred(nextSegment.startPos, nextSegment.endPos);
			};
		}

		// Play the "Mini accepted" pill sliding out of the current toolbar, then run
		// the advance a beat later (the mini owns that timing). Fall back to an
		// immediate advance when the toolbar element can't be located (e.g. in tests).
		const modalEl = editorView.dom.ownerDocument.querySelector(MODAL_CLASS_SELECTOR);
		if (!(modalEl instanceof HTMLElement)) {
			doMove();
			return;
		}
		const rect = modalEl.getBoundingClientRect();
		miniKeyRef.current += 1;
		setMiniAccepted({
			centerX: rect.left + rect.width / 2,
			// Anchor at the toolbar's top edge; the mini slides upward out of it.
			anchorY: rect.top,
			onSlideOut: doMove,
			key: miniKeyRef.current,
		});
	}, [
		editorView,
		isAggregate,
		acceptAndConfirm,
		currentSegment,
		hideDiff,
		displaySegments,
		segmentIndex,
		scrollRangeIntoViewDeferred,
	]);

	// Non-toggling: render the diff overlay for the current view (aggregate = all
	// segments, per-segment = the current segment). `hideDeleted` controls whether
	// deleted content is suppressed. Safe to call when a diff is already shown —
	// `showDiff` replaces the existing decorations.
	const showCurrentDiff = useCallback(
		(hideDeleted: boolean) => {
			// Aggregate mode diffs ALL coarse changes and does NOT depend on
			// `currentSegment` (which may be undefined if `segmentIndex` is stale).
			// Only step-through requires a valid `currentSegment`.
			if (!editorView || (!isAggregate && !activeStepSegment) || !showDiffCommands?.showDiff) {
				return;
			}
			// Aggregate diffs all visible (non-accepted) changes; step-through diffs
			// only the current visible segment.
			const result = isAggregate
				? buildDiffParamsForAllSegments(editorView, displaySegments)
				: buildDiffParamsForSegment(editorView, currentSegment);
			if (!result?.steps || result.steps.length === 0 || !result.originalDoc) {
				return;
			}
			coreActions?.execute(
				showDiffCommands.showDiff({
					originalDoc: result.originalDoc,
					steps: result.steps,
					// The screen owns its own scrolling (`scrollAnchorIntoView` below):
					// show-diff's `scrollIntoView` always force-scrolls to the FIRST
					// change even when it is already on screen, which yanks the page to
					// the top of the review. We keep the modal's current anchor in view
					// instead, only when it is off screen.
					scrollIntoView: false,
					isInverted: false,
					// When `true`, deleted content is suppressed and only added/updated
					// content is highlighted — the default review view. "Compare with
					// original" flips this to `false` to also reveal deletions.
					hideDeletedDiffs: hideDeleted,
					hideAddedDiffsUnderline: hideDeleted,
					// Render the inline add/remove indicators in the diff
					// overlay. The show-diff plugin internally gates actual rendering
					// behind `platform_editor_diff_plugin_extended`, so passing `true`
					// unconditionally is safe (no-op when that gate is off).
					showIndicators: true,
					// Always request the `smart` overlay. show-diff gates smart rendering
					// behind `platform_editor_ai_smart_diff` and falls back to inline when
					// that gate is off, which is still coherent.
					diffType: 'smart',
					// Render inline-level deleted content AFTER the added/updated content
					// (rather than before it) so reviewers read the new text first. show-diff
					// gates this behind `platform_editor_ai_smart_diff` + `diffType: 'smart'`,
					// so passing it unconditionally is safe (no-op when the gate is off), and
					// node/paragraph-level placement is unaffected.
					inlineDeletedDiffPlacement: 'after',
				}),
			);
		},
		[
			editorView,
			activeStepSegment,
			currentSegment,
			coreActions,
			showDiffCommands,
			isAggregate,
			displaySegments,
		],
	);

	// The change range the modal currently anchors below: the last still-visible
	// (non-accepted) change in aggregate (`segments` is sorted + disjoint, so the
	// last entry is furthest down), or the current segment in step-through.
	const anchoredChange = isAggregate ? displaySegments[displaySegments.length - 1] : currentSegment;

	// "View changes" no longer toggles the diff on/off — the diff is always shown.
	// It toggles whether DELETED content is revealed, re-issuing the diff overlay
	// with the flipped `hideDeletedDiffs`. Revealing deletions re-inserts removed
	// content and changes the layout, so re-scroll the anchored change back into
	// view (via the range helper, NOT show-diff's `scrollIntoView`, which would
	// jump to the FIRST change) so the modal stays below it.
	const onViewChanges = useCallback(() => {
		const next = !showDeletedChanges;
		setShowDeletedChanges(next);
		showCurrentDiff(!next);
		if (anchoredChange) {
			scrollRangeIntoViewDeferred(anchoredChange.startPos, anchoredChange.endPos);
		}
	}, [showDeletedChanges, showCurrentDiff, anchoredChange, scrollRangeIntoViewDeferred]);

	// Always-on diff: re-paint the diff whenever the view changes — open, Prev/Next
	// (`segmentIndex`), aggregate/step-through toggle (`isAggregate`), or when
	// segments first appear. The toggle's DELETED-content visibility PERSISTS across
	// these transitions, so the repaint honours the current
	// `showDeletedChanges` value rather than forcing the clean (deletions-hidden)
	// view. `showDeletedChanges` is intentionally not a dep (the "Compare with
	// original" button drives explicit toggles via `onViewChanges`); it is read via
	// a ref so a newly-navigated view reflects the persisted state without re-firing
	// this effect. `showCurrentDiff` is likewise read via a ref to avoid re-firing on
	// its frequently-changing deps.
	//
	// Note: `isStepThroughExpanded` and `showDeletedChanges` are intentionally not
	// reset on navigation — the step-through tray stays open and the chosen view is
	// preserved while the user steps through changes.
	const showCurrentDiffRef = useRef(showCurrentDiff);
	showCurrentDiffRef.current = showCurrentDiff;
	const showDeletedChangesRef = useRef(showDeletedChanges);
	showDeletedChangesRef.current = showDeletedChanges;
	useEffect(() => {
		// Once accepted the diff is intentionally hidden (see `acceptAndConfirm`) so the
		// author sees the final clean content under the "Accepted" pill — don't repaint it.
		if (isAccepted || !canViewChanges) {
			return;
		}
		// `showCurrentDiff(hideDeletedDiffs)` — hide deletions unless the user has the
		// toggle on, so the persisted view is reproduced on the newly-navigated segment.
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		showCurrentDiffRef.current(!showDeletedChangesRef.current);
		// `appliedIndexSignature` re-fires this after an undo/redo/rework so the diff
		// `hideDiff()` tore down is repainted at the new applied slice — unless the
		// segment is now fully undone (`canViewChanges` false), where the gray
		// "undone" decoration takes over.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		segmentIndex,
		isAggregate,
		displaySegments.length,
		appliedIndexSignature,
		canViewChanges,
		isAccepted,
	]);

	// Aggregate mode anchors the modal below the LAST change, which can sit off
	// screen when the edits span more than a viewport (first open) or after
	// collapsing step-through. Bring that last change into view — but only in
	// aggregate, and only when it is not already visible.
	//
	// Entering step-through deliberately does NOT scroll: the user is already
	// looking at the last change (that is where aggregate is anchored), so the
	// viewport must stay put. Step-through navigation (Prev/Next) drives its own
	// scroll via `useSegmentNavigation`. Keying only on `isAggregate` +
	// `segments.length` (never the debounced `segmentIndex`) means the stale
	// intermediate frame during the mode flip cannot trigger a spurious scroll.
	useEffect(() => {
		// No scrolling once accepted — the pill is showing and teardown is imminent.
		if (isAccepted || !isAggregate || displaySegments.length === 0) {
			return;
		}
		// `segments` are sorted by `startPos` and disjoint, so the last visible
		// (non-accepted) entry is furthest down the document — the change the modal
		// anchors below.
		const lastChange = displaySegments[displaySegments.length - 1];
		scrollRangeIntoViewDeferred(lastChange.startPos, lastChange.endPos);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAggregate, displaySegments.length, scrollRangeIntoViewDeferred, isAccepted]);

	useHideDiffOnUnmount(coreActions, showDiffCommands?.hideDiff);

	// Navigate the visible `segments` so Prev/Next skip accepted segments.
	const { goToPrevious, goToNext, canGoPrevious, canGoNext } = useSegmentNavigation({
		editorView,
		segmentIndex,
		segments: displaySegments,
		onScrollToSegment: scrollRangeIntoViewDeferred,
	});

	// [CCI-18059] Toolbar keyboard shortcuts (see `useReviewKeyboardShortcuts` for
	// the bindings). Wires the SAME handlers the buttons / click-away use. Escape
	// maps to `acceptAndConfirm` so it matches clicking outside the toolbar.
	useReviewKeyboardShortcuts(screenRef, {
		isAccepted,
		isPromptEmpty: !hasInputText,
		onAccept: acceptCurrent,
		onClose: acceptAndConfirm,
		canUndo,
		canRedo,
		onUndo,
		onRedo,
		isStepThroughExpanded,
		canGoPrevious,
		canGoNext,
		onPrevious: goToPrevious,
		onNext: goToNext,
	});

	const decoration = findModalDecoration(modalDecorationSet);
	if (!decoration || !modalDecorationSet) {
		return null;
	}

	// The editor's plugin-components container (inside `.ak-editor-content-area`, a
	// non-editable wrapper) — the Mini accepted pill portals here rather than into the
	// ProseMirror contentEditable, using the shared editor helper.
	const miniAcceptedContainer = getPopupContainerFromEditorView(editorView?.dom);

	return (
		<div ref={screenRef} css={wrapperCSS} data-testid="post-stream-review-session-screen">
			<ModalRegionErrorBoundary inContentComponent onCloseFallback={endExperience}>
				<ModalRegion
					lastTriggeredFrom={lastTriggeredFrom}
					key={modalMountedTimeStamp}
					decoration={decoration}
					editorView={editorView}
					editorRelativeWrapper={wrapperElement?.offsetParent}
					endExperience={endExperience}
					appearance={appearance}
					decorationSet={modalDecorationSet}
					isDirectionalPinningEnabled
				>
					{isAccepted ? (
						<AcceptedToast onDone={finalizeClose} />
					) : (
						<div css={toolbarCSS}>
							<AskRovo
								hasRovoPrompt={hasRovoPrompt}
								hasFallbackPrompt={hasFallbackPrompt}
								hasInputText={hasInputText}
								openInRovoChat={configItem?.openInRovoChat}
								openInRovoChatConfig={openInRovoChatConfig}
								cancel={cancel}
								PromptEditor={PromptEditor}
								textInput={textInput}
								onTextChange={onTextChange}
								onPromptContentChange={onPromptContentChange}
								submit={submit}
								appearance={appearance}
								isAIFC={isInDocumentStreamingEnabled}
								canUndo={canUndo}
								canRedo={canRedo}
								onUndo={onUndo}
								onRedo={onRedo}
								canViewChanges={canViewChanges}
								showDeletedChanges={showDeletedChanges}
								onViewChanges={onViewChanges}
								totalSegments={totalSegments}
								segmentIndex={segmentIndex}
								canGoPrevious={canGoPrevious}
								canGoNext={canGoNext}
								onPrevious={goToPrevious}
								onNext={goToNext}
								isStepThroughExpanded={isStepThroughExpanded}
								onToggleStepThrough={onToggleStepThrough}
							/>
						</div>
					)}
				</ModalRegion>
			</ModalRegionErrorBoundary>
			{miniAccepted && (
				<MiniAcceptedToast
					key={miniAccepted.key}
					centerX={miniAccepted.centerX}
					anchorY={miniAccepted.anchorY}
					container={miniAcceptedContainer}
					onSlideOut={miniAccepted.onSlideOut}
					onDone={clearMiniAccepted}
				/>
			)}
		</div>
	);
}
